import React, { useState, useEffect } from 'react';
import {
  List,
  Filter,
  Datagrid,
  TextField,
  DateField,
  SearchInput,
  EditButton,
  Pagination,
  FilterList,
  FilterListItem,
} from 'react-admin';
import { Card, CardContent } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import ToggleRowStyle from './Common';
import BulkInviteButton from './BulkInviteButton';
import { supabaseClient } from './supabase';

// Grade filter sidebar
const GradeFilterSidebar = ({ householdGrades }) => {
  const grades = [1, 2, 3, 4, 5, 6, 7];

  return (
    <Card sx={{ order: -1, mr: 2, mt: 6, width: 200, flexShrink: 0 }}>
      <CardContent sx={{ pt: 1 }}>
        <FilterList label="Graad" icon={<SchoolIcon />}>
          {grades.map(grade => {
            // Count parents with children in this grade
            const count = householdGrades
              ? Object.values(householdGrades).filter(g => g.includes(grade)).length
              : 0;
            return (
              <FilterListItem
                key={grade}
                label={`Graad ${grade} (${count})`}
                value={{ grade: grade }}
              />
            );
          })}
        </FilterList>
        <FilterList label="Status" icon={<PersonIcon />} sx={{ mt: 2 }}>
          <FilterListItem label="Alle" value={{ status: 'all' }} />
          <FilterListItem label="Aktief" value={{ status: 'active' }} />
          <FilterListItem label="Onaktief" value={{ status: 'inactive' }} />
        </FilterList>
      </CardContent>
    </Card>
  );
};

const ParentFilter = (props) => (
  <Filter {...props}>
    <SearchInput
      placeholder="Parent last name"
      source="last_name"
      resettable
      alwaysOn
    />
    <SearchInput
      placeholder="Parent first name"
      source="first_name"
      resettable
      alwaysOn
    />
    <SearchInput
      placeholder="Parent email address"
      source="email"
      resettable
      alwaysOn
    />
  </Filter>
);

// Custom list component that handles client-side filtering
const ParentListContent = ({ householdGrades, ...props }) => {
  return (
    <List
      {...props}
      filters={<ParentFilter />}
      aside={<GradeFilterSidebar householdGrades={householdGrades} />}
      title="List of parents"
      filterDefaultValues={{ status: 'all' }}
    >
      <Pagination />
      <Datagrid
        rowClick="expand"
        rowStyle={ToggleRowStyle}
        bulkActionButtons={<BulkInviteButton />}
      >
        <TextField source="last_name" />
        <TextField source="first_name" />
        <TextField source="email" />
        <DateField source="invited_at" label="Invited At" sortable={false} />
        <DateField
          source="last_sign_in_at"
          label="Last sign-in"
          sortable={false}
        />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const ParentList = (props) => {
  const [householdGrades, setHouseholdGrades] = useState(null);

  // Fetch students to build household -> grades mapping
  useEffect(() => {
    const fetchStudentGrades = async () => {
      const { data: students, error } = await supabaseClient
        .from('students')
        .select('household_hash, grade')
        .lt('grade', 99); // Exclude alumni

      if (!error && students) {
        // Build mapping: household_hash -> array of grades
        const mapping = {};
        students.forEach(student => {
          if (!mapping[student.household_hash]) {
            mapping[student.household_hash] = [];
          }
          if (!mapping[student.household_hash].includes(student.grade)) {
            mapping[student.household_hash].push(student.grade);
          }
        });
        setHouseholdGrades(mapping);
      }
    };

    fetchStudentGrades();
  }, []);

  return <ParentListContent householdGrades={householdGrades} {...props} />;
};

export default ParentList;
